import React, {
  useState,
  useMemo,
  useRef,
  forwardRef,
  startTransition,
  useCallback,
} from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { graphql, useFragment } from 'react-relay'
import type { ReferenceType } from '@floating-ui/react'

import {
  useNavigate,
  useLocation,
  useMatch,
  useSearchParams,
} from 'react-router-dom'

import {
  YStack,
  XStack,
  View,
  DialogHandle,
  AnimatePresence,
  isWeb,
  TamaguiElement,
  useSelectContext,
  useSelectItemParentContext,
  composeRefs,
} from 'tamagui'

import { useAuthContext } from '../../data/useAuthContext'
import { useMutation } from '../../data/useMutation'
import { useFlashMessage } from '../DynamicIsland/useFlashMessage'

import { Tooltip } from '../Tooltip'
import { Card } from '../Card'
import { H3, Text, Paragraph } from '../Text'
import { FormattedMarkdown } from '../FormattedMarkdown/FormattedMarkdown'
import { Link } from '../Link'
import { Icon } from '../Icon'
import { Image } from '../Image'
import { Button } from '../Button/Button'
import { Dialog } from '../Dialog/Dialog'
import {
  StyledSelect,
  StyledSelectContent,
  StyledSelectItem,
  StyledSelectItemText,
  StyledSelectViewport,
} from '../Form/FormSelect'

import { GuildPrimaryPhoto } from './GuildPrimaryPhoto'

import { GuildSocialProfileLinks } from '../SocialProfileLinks/GuildSocialProfileLinks'

import {
  GuildCardNew_guild$data,
  GuildCardNew_guild$key,
} from './__generated__/GuildCardNew_guild.graphql'
import { GuildCardPendingWithdrawMutation } from './__generated__/GuildCardPendingWithdrawMutation.graphql'

const ALLOWED_ELEMENTS = ['a', 'p']

export const GuildCard = ({
  guild,
  showSettingsButton = false,
  showMoreDialog = false,
  limitDescriptionLines,
  expand = false,
  isLCP = false,
  ...props
}: {
  guild: GuildCardNew_guild$key
  showSettingsButton?: boolean
  showMoreDialog?: boolean
  limitDescriptionLines?: number
  expand?: boolean
  isLCP?: boolean
}) => {
  const data = useFragment(
    graphql`
      fragment GuildCardNew_guild on Guild {
        rowId
        slugId

        name
        description

        ...GuildSocialProfileLinks_guild

        type

        myMembership {
          membershipLevel
          status
        }

        memberCount

        ...GuildPrimaryPhotoNew_guild

        backgroundPhoto {
          # Background photo: 500x200 cover crop
          transformUrl(transform: { w: 500, h: 200, dpr: 3, fit: "cover" })
        }

        # Signed URL for default background when no custom background is set
        defaultBackgroundUrl(transform: { w: 500, h: 200, dpr: 3 })
      }
    `,
    guild
  )

  const isGuildScreen = useMatch(`/${data.slugId}/*`)
  const navigate = useNavigate()

  const dialogRef = useRef<DialogHandle>(null)

  const [descriptionHovered, setDescriptionHovered] = useState(false)

  const guildCardDialogInner = (
    <>
      <GuildCardPhotos guild={data} />

      <YStack gap='$4' paddingVertical='$4' paddingHorizontal='$5'>
        <Dialog.Title size='$6'>{data.name}</Dialog.Title>

        <Dialog.Description>
          <YStack>
            <FormattedMarkdown
              allowedElements={ALLOWED_ELEMENTS}
              formattedVariant='PLAIN_TEXT'
              textSize='$4'
            >
              {data.description}
            </FormattedMarkdown>
          </YStack>
        </Dialog.Description>

        <XStack
          alignItems='center'
          justifyContent='space-between'
          height={16}
          marginTop='$1'
        >
          <XStack gap='$1'>
            <Text
              size='$4'
              fontWeight='$7'
              style={{
                fontVariantNumeric: 'tabular-nums slashed-zero',
                letterSpacing: -0.3,
              }}
            >
              {data.memberCount >= 1000
                ? `${(data.memberCount / 1000).toFixed(1).replace('.0', '')}K`
                : data.memberCount}
            </Text>
            <Text size='$4' color='$colorMuted'>
              {data.memberCount === 1 ? (
                <Trans>Member</Trans>
              ) : (
                <Trans>Members</Trans>
              )}
            </Text>
          </XStack>

          <GuildCardButton
            showSettingsButton={showSettingsButton}
            data={data}
            onPress={() => {
              dialogRef.current?.open(false)
            }}
          />
        </XStack>
      </YStack>
    </>
  )

  const expandMarkdownChildren = !!showMoreDialog && (
    <>
      <XStack justifyContent='center' marginTop={-28 / 2} height={28 / 2}>
        <Tooltip placement='top' delay={100}>
          <Tooltip.Trigger tag='dialog'>
            <Dialog ref={dialogRef} modal>
              <Dialog.Trigger
                aria-label={`Expand Description for ${data.name}`}
              >
                <AnimatePresence>
                  {descriptionHovered && (
                    <View
                      animation='mediumSpring'
                      backgroundColor='$backgroundHover'
                      // borderRadius='50%'
                      borderRadius='$2'
                      cursor='pointer'
                      opacity={0.8}
                      style={{
                        backdropFilter: 'blur(8px) saturate(250%)',
                        WebkitBackdropFilter: 'blur(8px) saturate(250%)',
                      }}
                      enterStyle={{ opacity: 0, y: -10, scale: 0.9 }}
                      exitStyle={{ opacity: 0, y: -10, scale: 0.9 }}
                      hoverStyle={{
                        opacity: 1,

                        backgroundColor: '$backgroundStrong',
                      }}
                    >
                      <Icon
                        padding={2}
                        // name='arrow-down-filled'
                        name='window-new'
                        size='$7'
                        color='$colorMuted'
                        // @ts-expect-error color is passed to styled children
                        hoverStyle={{ color: '$color' }}
                      />
                    </View>
                  )}
                </AnimatePresence>
              </Dialog.Trigger>

              <Dialog.Adapt when='xs'>
                <Dialog.Sheet key='sheet'>
                  <Dialog.Sheet.Handle zIndex={200_000} />
                  <Dialog.Sheet.Frame
                    padding={0}
                    gap='$4'
                    shadowColor='$shadowColor'
                    shadowRadius='$2'
                    borderTopRightRadius='$6'
                    borderTopLeftRadius='$6'
                  >
                    <Dialog.Sheet.ScrollView
                      // borderTopRightRadius='$6'
                      // borderTopLeftRadius='$6'
                      paddingTop={0}
                      paddingHorizontal={0}
                    >
                      <YStack gap='$4'>
                        {isWeb ? (
                          guildCardDialogInner
                        ) : (
                          <Dialog.Adapt.Contents />
                        )}
                      </YStack>
                    </Dialog.Sheet.ScrollView>
                  </Dialog.Sheet.Frame>
                </Dialog.Sheet>
              </Dialog.Adapt>

              <Dialog.Portal>
                <Dialog.Content
                  elevate
                  key='content'
                  // gap='$4'
                  $gtXs={{
                    minWidth: 300,
                    maxWidth: 400,
                    minHeight: 300,
                    maxHeight: 600,
                  }}
                  padding={0}
                  forceMount
                >
                  {guildCardDialogInner}
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog>
          </Tooltip.Trigger>

          <Tooltip.Content>
            <Tooltip.Arrow />

            <Paragraph size='$4' color='$colorOnPrimary' fontWeight='$5'>
              <Trans>Expand</Trans>
            </Paragraph>
          </Tooltip.Content>
        </Tooltip>
      </XStack>
    </>
  )

  const content = (
    <Card {...props} glass padding={0} flex={expand ? 1 : undefined}>
      <GuildCardPhotos guild={data} isLCP={isLCP} />

      <YStack
        gap='$4'
        paddingVertical='$4'
        paddingHorizontal='$5'
        flex={expand ? 1 : undefined}
      >
        <H3 size='$6'>{data.name}</H3>

        {data.description ? (
          <YStack
            flex={expand ? 1 : undefined}
            onMouseEnter={() => {
              setDescriptionHovered(true)
            }}
            onMouseLeave={() => {
              setDescriptionHovered(false)
            }}
          >
            <FormattedMarkdown
              allowedElements={ALLOWED_ELEMENTS}
              formattedVariant='PLAIN_TEXT'
              textSize='$4'
              numberOfLines={limitDescriptionLines}
              expandMarkdownChildren={expandMarkdownChildren}
            >
              {data.description}
            </FormattedMarkdown>
          </YStack>
        ) : (
          <YStack flex={expand ? 1 : undefined} />
        )}

        <GuildSocialProfileLinks guild={data} />

        <XStack
          alignItems='center'
          justifyContent='space-between'
          height={16}
          marginTop='$1'
        >
          <XStack gap='$1' alignItems='center'>
            <Text
              size='$4'
              fontWeight='$7'
              style={{
                fontVariantNumeric: 'tabular-nums slashed-zero',
                letterSpacing: -0.3,
              }}
            >
              {data.memberCount >= 1000
                ? `${(data.memberCount / 1000).toFixed(1).replace('.0', '')}K`
                : data.memberCount}
            </Text>
            <Text size='$4' color='$colorMuted'>
              {data.memberCount === 1 ? (
                <Trans>Member</Trans>
              ) : (
                <Trans>Members</Trans>
              )}
            </Text>
          </XStack>

          <GuildCardButton
            showSettingsButton={showSettingsButton}
            data={data}
          />
        </XStack>
      </YStack>
    </Card>
  )

  if (isGuildScreen) {
    return content
  }

  return (
    <View
      display='flex'
      flex={expand ? 1 : undefined}
      flexDirection='column'
      cursor='pointer'
      role='link'
      tabIndex={0}
      aria-label={data.name ?? undefined}
      onPress={() => startTransition(() => navigate(`/${data.slugId}`))}
      // @ts-expect-error color is needed for text inheritance on web
      color='$colorStrong'
    >
      {content}
    </View>
  )
}

const GuildCardPhotos = ({
  guild,
  isLCP = false,
}: {
  guild: GuildCardNew_guild$data
  isLCP?: boolean
}) => {
  const displayedBackground = useMemo(() => {
    // Use custom background if available, otherwise use server-signed default
    return guild.backgroundPhoto?.transformUrl ?? guild.defaultBackgroundUrl
  }, [guild.backgroundPhoto, guild.defaultBackgroundUrl])

  return (
    <YStack maxWidth='100%'>
      <Image
        alt={`Cover Photo for ${guild.name}`}
        aspectRatio={500 / 200}
        src={displayedBackground}
        borderTopLeftRadius='$6'
        borderTopRightRadius='$6'
        maxHeight={30 * 4}
        style={{ objectFit: 'cover' }}
        maxWidth='100%'
        {...(isLCP ? { loading: 'eager', fetchPriority: 'high' } : {})}
      />

      <XStack
        marginTop={-40}
        paddingHorizontal='$5'
        gap='$4'
        alignItems='flex-end'
      >
        <GuildPrimaryPhoto
          guild={guild}
          size='$20'
          borderRadius='$4'
          // @ts-expect-error boxShadow is web-only
          boxShadow='0 0 0 3px var(--background)'
        />

        {guild.type === 'NETWORK' && (
          <XStack marginBottom='$2'>
            <Text
              size='$4'
              fontWeight='$6'
              backgroundColor='$accentSurface'
              color='$colorOnPrimary'
              borderRadius='$4'
              paddingVertical='$1'
              paddingHorizontal='$3'
            >
              <Trans>Network</Trans>
            </Text>
          </XStack>
        )}
      </XStack>
    </YStack>
  )
}

const GuildCardButton = ({
  showSettingsButton,
  data,
  onPress,
}: {
  showSettingsButton: boolean
  data: GuildCardNew_guild$data
  onPress?: () => void
}) => {
  const { t } = useLingui()
  const { accessToken } = useAuthContext()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as
    | { backgroundLocation?: Location }
    | undefined
  const [searchParams] = useSearchParams()

  const tooltipText = useMemo(() => {
    switch (data.myMembership?.membershipLevel) {
      case 'ORGANIZER': {
        return t`You're an organizer`
      }
      case 'EDITOR': {
        return t`You're an editor`
      }
      case 'MEMBER': {
        return t`You're a member`
      }
    }

    return ''
  }, [data.myMembership, t])

  if (!data.myMembership || data.myMembership.status === 'DENIED') {
    return (
      <Button
        borderRadius={15}
        marginRight={-11}
        height={30}
        minHeight={30}
        paddingLeft='$2'
        paddingRight='$3'
        onPress={(event) => {
          event.preventDefault()
          event.stopPropagation()

          const backgroundLocation =
            locationState?.backgroundLocation || location

          // Preserve redirect param (e.g. from ticket claim flow)
          const redirect = searchParams.get('redirect')
          const confirmPath = redirect
            ? `/${data.slugId}/confirm?redirect=${encodeURIComponent(redirect)}`
            : `/${data.slugId}/confirm`

          if (accessToken) {
            navigate(confirmPath, {
              state: { backgroundLocation },
            })
          } else {
            navigate('/sign-up', {
              state: {
                backgroundLocation,
                authRedirectPath: confirmPath,
              },
            })
          }

          if (onPress) {
            onPress()
          }
        }}
        secondary
      >
        <XStack gap='$1' alignItems='center'>
          <Icon size='$4' name='chevron-right' />

          <Button.Text size='$4' secondary>
            <Trans>Join</Trans>
          </Button.Text>
        </XStack>
      </Button>
    )
  }

  if (data.myMembership.status === 'PENDING') {
    return <PendingWithdrawButton data={data} />
  }

  if (showSettingsButton) {
    return (
      <Link
        href={`/${data.slugId}/settings`}
        hoverStyle={{ textDecorationLine: 'none' }}
        onPress={onPress}
      >
        <Button
          borderRadius={15}
          marginRight={-11}
          height={30}
          minHeight={30}
          paddingRight='$3'
          paddingLeft='$2'
          secondary
        >
          <XStack gap='$1' alignItems='center'>
            <Icon size='$4' name='settings-filled' />

            <Button.Text size='$4' secondary>
              <Trans>Settings</Trans>
            </Button.Text>
          </XStack>
        </Button>
      </Link>
    )
  }

  return (
    <Tooltip placement='top' delay={100}>
      <Tooltip.Trigger tag='dialog'>
        <Icon size='$4' name='checkmark-filled' />
      </Tooltip.Trigger>

      <Tooltip.Content
        animation='fastSpring'
        enterStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
        exitStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
        scale={1}
        x={0}
        y={0}
        opacity={1}
        borderColor='rgba(105, 102, 118, 1.0)'
        backgroundColor='rgba(105, 102, 118, 0.8)'
        style={{
          backdropFilter: 'blur(8px) saturate(250%)',
          WebkitBackdropFilter: 'blur(8px) saturate(250%)',
        }}
      >
        <Tooltip.Arrow
          borderColor='rgba(105, 102, 118, 1.0)'
          backgroundColor='rgba(105, 102, 118, 0.7)'
          style={{
            backdropFilter: 'blur(8px) saturate(250%)',
            WebkitBackdropFilter: 'blur(8px) saturate(250%)',
          }}
        />

        <Paragraph size='$4' color='$colorOnPrimary' fontWeight='$5'>
          {tooltipText}
        </Paragraph>
      </Tooltip.Content>
    </Tooltip>
  )
}

const PendingWithdrawButton = ({ data }: { data: GuildCardNew_guild$data }) => {
  const { t } = useLingui()
  const { showFlash } = useFlashMessage()

  const leaveGuild = useMutation<GuildCardPendingWithdrawMutation>(graphql`
    mutation GuildCardPendingWithdrawMutation($input: LeaveGuildInput!) {
      leaveGuild(input: $input) {
        guild {
          ...GuildCardNew_guild
        }
      }
    }
  `)

  return (
    <StyledSelect
      onValueChange={async (value: string) => {
        if (value === 'withdraw') {
          try {
            await leaveGuild({
              variables: { input: { guildId: data.rowId } },
            })
            showFlash(t`Application withdrawn`, 'success')
          } catch (err) {
            showFlash(t`Failed to withdraw application`, 'error')
          }
        }
      }}
    >
      <PendingSelectTrigger />

      <StyledSelectContent zIndex={200_000}>
        <StyledSelectViewport>
          <StyledSelectItem index={0} value='withdraw'>
            <StyledSelectItemText>
              <Trans>Withdraw Application</Trans>
            </StyledSelectItemText>
          </StyledSelectItem>
        </StyledSelectViewport>
      </StyledSelectContent>
    </StyledSelect>
  )
}

const PendingSelectTrigger = forwardRef<TamaguiElement>((props, ref) => {
  const selectContext = useSelectContext()
  const itemParentContext = useSelectItemParentContext()
  const referenceProps =
    itemParentContext?.interactions?.getReferenceProps() as
      | React.ComponentProps<typeof Button>
      | undefined
  const buttonRef = useRef<TamaguiElement>(null)
  const setFloatingReference = useCallback(
    (node: TamaguiElement | null) => {
      selectContext?.floatingContext?.refs.setReference(
        node as unknown as ReferenceType | null
      )
    },
    [selectContext]
  )

  return (
    <Button
      ref={composeRefs(ref, setFloatingReference, buttonRef)}
      {...referenceProps}
      borderRadius={15}
      marginRight={-11}
      height={30}
      minHeight={30}
      paddingLeft='$2'
      paddingRight='$3'
      secondary
      onPress={(e) => {
        e.preventDefault()
        e.stopPropagation()
        itemParentContext?.setOpen(!selectContext?.open)
      }}
    >
      <XStack gap='$1' alignItems='center'>
        <Icon name='clock' size='$4' color='$colorMuted' />
        <Button.Text size='$4' secondary>
          <Trans>Pending</Trans>
        </Button.Text>
      </XStack>
    </Button>
  )
})
PendingSelectTrigger.displayName = 'PendingSelectTrigger'
